import { useState } from 'react';
import { Badge, Button, Checkbox, FilterChips, Footnote, SectionCard, WhatsAppPreview, type ChipOption } from '../../../components/ui';
import { formatCount, maskPhoneDisplay } from '../../../lib/format';
import type { EstimativaEnvio } from '../estimativa';
import type { FormularioEnvio, PublicoCalculado } from '../formulario';
import type { MessageTemplateSummary, SellerOption, SenderOption, WhatsAppTariffs } from '../types';
import { EstimativaDoEnvio } from './EstimativaDoEnvio';

/**
 * Coluna direita do novo envio: como a mensagem chega, o que custa e o que
 * deve voltar, e os botoes de acao.
 *
 * Decisoes nao obvias:
 *  1. A previa alterna entre "Variáveis" (quem escreve ve o que sera trocado)
 *     e "Exemplo" (como um contato real le, com valores de exemplo da
 *     empresa). Os valores vem do servidor (`sample`), nao do catalogo, para
 *     a troca funcionar igual em producao.
 *  2. "Agendar" e "Iniciar" compartilham as mesmas pendencias: agendar um
 *     envio incompleto so adiaria o problema. A lista do que falta e escrita
 *     por extenso embaixo dos botoes.
 *  3. A estimativa fica ENTRE a previa e os botoes: a pessoa ve custo e
 *     retorno antes de clicar, nunca depois.
 */

type ModoPrevia = 'variaveis' | 'exemplo';

const MODOS: readonly ChipOption<ModoPrevia>[] = [
  { value: 'variaveis', label: 'Variáveis' },
  { value: 'exemplo', label: 'Com um exemplo' },
];

function preencher(texto: string, valores: Readonly<Record<string, string>>): string {
  return texto.replace(/\{([a-z_]+)\}/g, (inteiro, nome: string) => valores[nome] ?? inteiro);
}

export function PreviaDoEnvio({
  companyName,
  template,
  sender,
  sample,
  estimativa,
  publico,
  tariffs,
  feeBps,
  sellers,
  form,
  pendencias,
  onChange,
  onEnviarTeste,
  onAgendar,
  onIniciar,
  feedback,
  className = '',
}: {
  readonly companyName: string;
  readonly template: MessageTemplateSummary | null;
  readonly sender: SenderOption | null;
  readonly sample: Readonly<Record<string, string>>;
  readonly estimativa: EstimativaEnvio;
  readonly publico: PublicoCalculado;
  readonly tariffs: WhatsAppTariffs;
  readonly feeBps: number;
  readonly sellers: readonly SellerOption[];
  readonly form: FormularioEnvio;
  readonly pendencias: readonly string[];
  readonly onChange: (patch: Partial<FormularioEnvio>) => void;
  readonly onEnviarTeste: () => void;
  readonly onAgendar: () => void;
  readonly onIniciar: () => void;
  readonly feedback: string | null;
  readonly className?: string;
}): JSX.Element {
  const [modo, setModo] = useState<ModoPrevia>('variaveis');
  const completo = pendencias.length === 0;
  const exemplo = modo === 'exemplo';

  return (
    <div className={`flex flex-col gap-6 ${className}`}>
      <SectionCard
        id="previa-envio"
        icon="whatsapp"
        title="Como chega no WhatsApp"
        aside={
          <Badge tone={estimativa.contatos > 0 ? 'brand' : 'neutral'} icon="leads">
            Contatos: {formatCount(estimativa.contatos)}
          </Badge>
        }
      >
        <div className="flex flex-col gap-3">
          <FilterChips options={MODOS} value={modo} onChange={setModo} label="Modo da prévia" prefix="Mostrar:" tone="accent" />
          {template === null ? (
            <div className="grid min-h-[14rem] place-items-center rounded-2xl border border-dashed border-edge bg-elevated px-6 text-center text-sm text-ink-subtle">
              Escolha um modelo e ele aparece aqui, do jeito que o contato vê.
            </div>
          ) : (
            <WhatsAppPreview
              from={companyName}
              body={exemplo ? preencher(template.body, sample) : template.body}
              buttons={template.buttons}
              footer={template.footer ?? undefined}
            />
          )}
          <Footnote>
            {sender === null
              ? 'Sem número de saída conectado.'
              : `Sai pelo ${maskPhoneDisplay(sender.phoneE164)}, o WhatsApp do comercial de ${companyName}. Quem responder cai com as vendedoras; quem responder SAIR nunca mais recebe envio desta empresa.`}
          </Footnote>
          <Button variant="secondary" icon="telefone" onClick={onEnviarTeste} disabled={template === null}>
            Enviar teste para a equipe
          </Button>
        </div>
      </SectionCard>

      <SectionCard id="antes-de-disparar" icon="moeda" title="Antes de disparar" description="Custo exato, retorno pelo histórico da empresa e duração no ritmo escolhido.">
        <div className="flex flex-col gap-4">
          <EstimativaDoEnvio
            estimativa={estimativa}
            publico={publico}
            senderKind={sender?.kind ?? null}
            tariffs={tariffs}
            feeBps={feeBps}
            sellers={sellers}
            vendedorasEscolhidas={form.vendedoras}
            janelaInicio={form.janelaInicio}
            janelaFim={form.janelaFim}
            viaCsv={form.fonte === 'csv'}
          />

          <Checkbox
            checked={form.consentimento}
            onChange={(v) => onChange({ consentimento: v })}
            label="Confirmo que o público já passou pelo fato (abandono, recusa, vencimento ou resposta) e que os contatos consentiram em receber mensagens da empresa"
          />

          <div className="flex flex-col gap-2 sm:flex-row">
            <Button variant="secondary" size="lg" icon="calendario" disabled={!completo} onClick={onAgendar} className="sm:flex-1">
              Agendar
            </Button>
            <Button variant="primary" size="lg" icon="disparo" disabled={!completo} onClick={onIniciar} className="sm:flex-1">
              Iniciar envio em massa
            </Button>
          </div>

          {completo ? (
            <p className="text-xs text-ink-subtle">
              Tudo pronto. O envio respeita o ritmo e a janela{form.pausarSeOptOutAlto ? ' e a trava de opt-out' : ' (trava de opt-out desligada)'}; dá para pausar a qualquer momento na lista.
            </p>
          ) : (
            <p className="text-xs text-ink-subtle">Para liberar: {pendencias.join(' · ')}.</p>
          )}

          {feedback === null ? null : (
            <p role="status" className="flex flex-wrap items-center gap-2 text-sm text-ink">
              <Badge tone="warning" dot>
                Simulado na prévia
              </Badge>
              {feedback}
            </p>
          )}
        </div>
      </SectionCard>
    </div>
  );
}
